import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminAuctions, createAuction, endAuction } from "../../redux/actions/auction";
import { toast } from "react-toastify";
import {
  HiOutlineLightningBolt,
  HiOutlinePlus,
  HiOutlineStop,
  HiOutlineClock,
} from "react-icons/hi";

const AdminAuctionManagement = () => {
  const dispatch = useDispatch();
  const { adminAuctions, isLoading, total, pages, success, error } = useSelector((state) => state.auctions);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    productId: "",
    title: "",
    description: "",
    startingPrice: "",
    reservePrice: "",
    minimumIncrement: "1",
    startTime: "",
    endTime: "",
  });

  useEffect(() => {
    dispatch(getAdminAuctions(statusFilter, page));
  }, [dispatch, statusFilter, page]);

  useEffect(() => {
    if (success) {
      toast.success("Auction action completed");
      dispatch({ type: "clearAuctionSuccess" });
      dispatch(getAdminAuctions(statusFilter, page));
      setShowCreateModal(false);
    }
    if (error) {
      toast.error(error);
      dispatch({ type: "clearAuctionErrors" });
    }
  }, [success, error]);

  const handleCreate = (e) => {
    e.preventDefault();
    dispatch(createAuction({
      ...formData,
      startingPrice: Number(formData.startingPrice),
      reservePrice: formData.reservePrice ? Number(formData.reservePrice) : null,
      minimumIncrement: Number(formData.minimumIncrement),
    }));
  };

  const handleEndAuction = (auctionId) => {
    if (window.confirm("Are you sure you want to end this auction?")) {
      dispatch(endAuction(auctionId));
    }
  };

  const statusBadge = (status) => {
    const colors = {
      scheduled: "bg-blue-100 text-blue-800",
      live: "bg-green-100 text-green-800 animate-pulse",
      ended: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1  text-xs font-medium ${colors[status] || "bg-gray-100"}`}>
        {status}
      </span>
    );
  };

  const timeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    if (diff <= 0) return "Ended";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen bg-secondary-50">
      <div className="mb-8 pb-6 border-b border-secondary-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin</p>
            <h1 className="text-3xl font-bold text-black">Auction Management</h1>
            <p className="text-secondary-500 text-sm mt-1">Create and manage product auctions</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex flex-wrap gap-1">
              {["", "scheduled", "live", "ended", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    statusFilter === s
                      ? "bg-black text-white"
                      : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                  }`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1 px-4 py-2 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Create Auction
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin  h-12 w-12 border-b-2 border-[#111111]" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {adminAuctions && adminAuctions.length > 0 ? adminAuctions.map((auction) => (
            <div key={auction._id} className="bg-white  shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm truncate pr-2">{auction.title}</h3>
                {statusBadge(auction.status)}
              </div>

              {auction.product?.images?.[0]?.url && (
                <img
                  src={auction.product.images[0].url}
                  alt=""
                  className="w-full h-32 object-cover  mb-3"
                />
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Product:</span>
                  <span className="text-gray-900 font-medium truncate ml-2">{auction.product?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Starting Price:</span>
                  <span className="text-gray-900">₹{auction.startingPrice}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Highest Bid:</span>
                  <span className="text-[#111111] font-bold">₹{auction.highestBid || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Bids:</span>
                  <span className="text-gray-900">{auction.totalBids || 0}</span>
                </div>
                {auction.status === "live" && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time Left:</span>
                    <span className="text-red-600 font-medium flex items-center gap-1">
                      <HiOutlineClock className="w-3.5 h-3.5" />
                      {timeRemaining(auction.endTime)}
                    </span>
                  </div>
                )}
                {auction.winner && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Winner:</span>
                    <span className="text-green-600 font-medium">{auction.winner?.name || "Selected"}</span>
                  </div>
                )}
              </div>

              {(auction.status === "live" || auction.status === "scheduled") && (
                <button
                  onClick={() => handleEndAuction(auction._id)}
                  className="w-full mt-3 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-600  text-sm font-medium hover:bg-red-100 transition-colors"
                >
                  <HiOutlineStop className="w-4 h-4" />
                  End Auction
                </button>
              )}
            </div>
          )) : (
            <div className="col-span-full text-center py-12 text-gray-500">
              No {statusFilter || ""} auctions found
            </div>
          )}
        </div>
      )}

      {/* Create Auction Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white  p-6 w-full max-w-lg mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Auction</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product ID</label>
                <input
                  type="text"
                  value={formData.productId}
                  onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                  className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  placeholder="Enter product ID (must have auction enabled)"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starting Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.startingPrice}
                    onChange={(e) => setFormData({ ...formData, startingPrice: e.target.value })}
                    className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reserve Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.reservePrice}
                    onChange={(e) => setFormData({ ...formData, reservePrice: e.target.value })}
                    className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Bid Increment (₹)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.minimumIncrement}
                  onChange={(e) => setFormData({ ...formData, minimumIncrement: e.target.value })}
                  className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full border border-gray-300  p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100  hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#111111]  hover:bg-[#1a1a1a]"
                >
                  Create Auction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuctionManagement;
